import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Button,
  Card,
  CardActionArea,
  Chip,
  Collapse,
  Fade,
  IconButton,
  Paper,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import QuizIcon from '@mui/icons-material/Quiz';
import LoadingButton from '../../Shared/Loading/index.tsx';
import QuestionCountSelector from '../../Shared/QuestionSelect/index.tsx';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';

const CARD_COLORS = [
  { bg: '#2196F3', border: '#2196F3', text: '#FFFFFF' },
  { bg: '#9C27B0', border: '#9C27B0', text: '#FFFFFF' },
  { bg: '#4CAF50', border: '#4CAF50', text: '#FFFFFF' },
  { bg: '#FF9800', border: '#FF9800', text: '#FFFFFF' },
  { bg: '#E91E63', border: '#E91E63', text: '#FFFFFF' },
  { bg: '#009688', border: '#009688', text: '#FFFFFF' },
  { bg: '#8BC34A', border: '#8BC34A', text: '#FFFFFF' },
  { bg: '#FF5722', border: '#FF5722', text: '#FFFFFF' },
];

interface QuestionType {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
}

interface DifficultyLevel {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

interface QuestionCount {
  id: string;
  label: string;
  value: number;
}

// ─── NEW: Prerequisite node type ──────────────────────────────────────────────
interface PrerequisiteNode {
  id: string;
  name: string;
  children: PrerequisiteNode[];
}

// ─── NEW: Subtopic with prerequisites ─────────────────────────────────────────
interface SubtopicWithPrereqs {
  subtopic: string;
  prerequisites: PrerequisiteNode[];
}

// ─── UPDATED: topic_sub_topic now carries prerequisites per subtopic ───────────
export interface TopicSubtopicWithPrereqs {
  [topic: string]: SubtopicWithPrereqs[];
}

interface SelectionFlowProps {
  knowledgeMapData: any[];
  selectedBoard: string;
  selectedClass: string;
  selectedSubject: string;
  // ─── UPDATED type ─────────────────────────────────────────────────────────
  selectedTopicSubtopics: TopicSubtopicWithPrereqs;
  selectedDifficulty: string[];
  selectedQuestionTypes: string[];
  selectedQuestionCount: string;
  setSelectedBoard: (board: string) => void;
  setSelectedClass: (cls: string) => void;
  setSelectedSubject: (subject: string) => void;
  // ─── UPDATED setter type ───────────────────────────────────────────────────
  setSelectedTopicSubtopics: React.Dispatch<React.SetStateAction<TopicSubtopicWithPrereqs>>;
  setSelectedDifficulty: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedQuestionTypes: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedQuestionCount: (count: string) => void;
  canGenerate: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
}

// ─── Helper: flatten prerequisite tree into ordered name array ────────────────
const flattenPrerequisites = (nodes: PrerequisiteNode[]): string[] => {
  const result: string[] = [];
  const traverse = (node: PrerequisiteNode) => {
    result.push(node.name);
    node.children.forEach(traverse);
  };
  nodes.forEach(traverse);
  return result;
};

const SelectionFlow: React.FC<SelectionFlowProps> = ({
  knowledgeMapData,
  selectedBoard,
  selectedClass,
  selectedSubject,
  selectedTopicSubtopics,
  selectedDifficulty,
  selectedQuestionTypes,
  selectedQuestionCount,
  setSelectedBoard,
  setSelectedClass,
  setSelectedSubject,
  setSelectedTopicSubtopics,
  setSelectedDifficulty,
  setSelectedQuestionTypes,
  setSelectedQuestionCount,
  canGenerate,
  isGenerating,
  onGenerate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());

  const questionTypes: QuestionType[] = [
    {
      id: 'mcq',
      label: 'MCQ',
      icon: <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />,
      description: 'Multiple Choice Questions',
    },
    {
      id: 'multiple-selection',
      label: 'Multiple Selection',
      icon: <CheckBoxIcon sx={{ fontSize: 18 }} />,
      description: 'Select Multiple Answers',
    },
    {
      id: 'boolean',
      label: 'True / False',
      icon: <ToggleOnIcon sx={{ fontSize: 18 }} />,
      description: 'Boolean Type Questions',
    },
    // {
    //   id: 'one-line',
    //   label: 'One Line',
    //   icon: <ShortTextIcon sx={{ fontSize: 18 }} />,
    //   description: 'Brief Single Line Answer',
    // },
    // {
    //   id: 'short-answer',
    //   label: 'Short Answer',
    //   icon: <NotesIcon sx={{ fontSize: 18 }} />,
    //   description: '2-3 Sentences Answer',
    // },
    // {
    //   id: 'long-answer',
    //   label: 'Long Answer',
    //   icon: <FormatListNumberedIcon sx={{ fontSize: 18 }} />,
    //   description: 'Detailed Explanation',
    // },
  ];

  const difficultyLevels: DifficultyLevel[] = [
    {
      id: 'easy',
      label: 'Easy',
      icon: <TrendingDownIcon sx={{ fontSize: 18 }} />,
      color: '#4CAF50',
    },
    {
      id: 'medium',
      label: 'Medium',
      icon: <TrendingFlatIcon sx={{ fontSize: 18 }} />,
      color: '#FF9800',
    },
    {
      id: 'hard',
      label: 'Hard',
      icon: <TrendingUpIcon sx={{ fontSize: 18 }} />,
      color: '#F44336',
    },
  ];

  const questionCounts: QuestionCount[] = [
    // { id: '1', label: '1 Question', value: 1 },
    { id: '5', label: '5 Questions', value: 5 },
    { id: '10', label: '10 Questions', value: 10 },
    { id: '20', label: '20 Questions', value: 20 },
    { id: '30', label: '30 Questions', value: 30 },
  ];

  const boards = useMemo(() => {
    if (!knowledgeMapData?.length) return [];
    const uniqueBoards = Array.from(new Set(knowledgeMapData.map((item: any) => item.board).filter(Boolean)));
    return uniqueBoards.map((board) => ({ id: board, label: board }));
  }, [knowledgeMapData]);

  const classes = useMemo(() => {
    if (!knowledgeMapData?.length || !selectedBoard) return [];
    const boardData = knowledgeMapData.filter((item: any) => item.board === selectedBoard);
    const uniqueClasses = Array.from(new Set(boardData.map((item: any) => item.class).filter(Boolean)));
    return uniqueClasses.map((cls) => ({ id: cls, label: `Class ${cls}` }));
  }, [knowledgeMapData, selectedBoard]);

  const subjects = useMemo(() => {
    if (!knowledgeMapData?.length || !selectedBoard || !selectedClass) return [];
    const filtered = knowledgeMapData.filter(
      (item: any) => item.board === selectedBoard && item.class === selectedClass
    );
    const uniqueSubjects = Array.from(new Set(filtered.map((item: any) => item.subject).filter(Boolean)));
    return uniqueSubjects.map((subject) => ({ id: subject, label: subject }));
  }, [knowledgeMapData, selectedBoard, selectedClass]);

  // ─── UPDATED: topics now carry raw prerequisite data per subtopic ────────────
  const topics = useMemo(() => {
    if (!knowledgeMapData?.length || !selectedBoard || !selectedClass || !selectedSubject) return [];
    const subjectData = knowledgeMapData.find(
      (item: any) => item.board === selectedBoard && item.class === selectedClass && item.subject === selectedSubject
    );
    if (!subjectData?.topics) return [];
    return subjectData.topics.map((topic: any) => ({
      id: topic.topic,
      label: topic.topic,
      subtopics:
        topic.subtopics?.map((sub: any) => ({
          id: sub.subtopic,
          label: sub.subtopic,
          // ─── Carry the full prerequisites array from the knowledge map ────
          prerequisites: (sub.prerequisites as PrerequisiteNode[]) || [],
        })) || [],
    }));
  }, [knowledgeMapData, selectedBoard, selectedClass, selectedSubject]);

  const toggleTopicExpansion = (topicId: string) => {
    setExpandedTopics((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(topicId)) {
        newSet.delete(topicId);
      } else {
        newSet.add(topicId);
      }
      return newSet;
    });
  };

  // ─── UPDATED: store { subtopic, prerequisites } objects per topic ────────────
  const handleSubtopicToggle = (
    topicId: string,
    subtopicId: string,
    prerequisites: PrerequisiteNode[]
  ) => {
    setSelectedTopicSubtopics((prev) => {
      const newSelection = { ...prev };

      if (newSelection[topicId]) {
        const existingIdx = newSelection[topicId].findIndex((s) => s.subtopic === subtopicId);

        if (existingIdx !== -1) {
          // Already selected → deselect
          const updated = newSelection[topicId].filter((s) => s.subtopic !== subtopicId);
          if (updated.length === 0) {
            delete newSelection[topicId];
          } else {
            newSelection[topicId] = updated;
          }
        } else {
          // Add new subtopic with its prerequisites
          newSelection[topicId] = [
            ...newSelection[topicId],
            { subtopic: subtopicId, prerequisites },
          ];
        }
      } else {
        // First subtopic for this topic
        newSelection[topicId] = [{ subtopic: subtopicId, prerequisites }];
      }

      return newSelection;
    });
  };

  const handleDifficultyToggle = (difficultyId: string) => {
    setSelectedDifficulty((prev) =>
      prev.includes(difficultyId) ? prev.filter((d) => d !== difficultyId) : [...prev, difficultyId]
    );
  };

  const handleQuestionTypeToggle = (typeId: string) => {
    setSelectedQuestionTypes((prev) =>
      prev.includes(typeId) ? prev.filter((id) => id !== typeId) : [...prev, typeId]
    );
  };

  // ─── UPDATED check helpers ────────────────────────────────────────────────────
  const isSubtopicSelected = (topicId: string, subtopicId: string) => {
    return selectedTopicSubtopics[topicId]?.some((s) => s.subtopic === subtopicId) || false;
  };

  const getTopicSelectionCount = (topicId: string) => {
    return selectedTopicSubtopics[topicId]?.length || 0;
  };

  const getTotalSelectedTopics = () => {
    return Object.keys(selectedTopicSubtopics).length;
  };

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 } }}>
      {/* Board Selection */}
      {!selectedBoard && (
        <Fade in={true}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mt: 2, color: '#2c3e50', fontSize: 15 }}>
              Select Board
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
              {boards.map((board, index) => {
                const color = CARD_COLORS[index % CARD_COLORS.length];
                return (
                  <Chip
                    key={board.id}
                    label={board.label}
                    onClick={() => setSelectedBoard(board.id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: color.bg,
                      color: color.text,
                      fontSize: 11,
                      height: 30,
                      px: 1.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: color.border,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 3px 8px ${color.border}40`,
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Fade>
      )}

      {/* Class Selection */}
      {selectedBoard && !selectedClass && (
        <Fade in={true}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
              <Chip label={selectedBoard} size="small" sx={{ bgcolor: '#2196F3', color: '#fff', fontSize: 10, height: 26 }} />
              <IconButton
                size="small"
                onClick={() => setSelectedBoard('')}
                sx={{ bgcolor: 'white', boxShadow: 1, width: 24, height: 24 }}
              >
                <EditIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: '#2c3e50', fontSize: 15 }}>
              Select Class
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
              {classes.map((classItem, index) => {
                const color = CARD_COLORS[(index + 1) % CARD_COLORS.length];
                return (
                  <Chip
                    key={classItem.id}
                    label={classItem.label}
                    onClick={() => setSelectedClass(classItem.id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: color.bg,
                      color: color.text,
                      fontSize: 11,
                      height: 30,
                      px: 1.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: color.border,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 3px 8px ${color.border}40`,
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Fade>
      )}

      {/* Subject Selection */}
      {selectedBoard && selectedClass && !selectedSubject && (
        <Fade in={true}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
              <Chip label={selectedBoard} size="small" sx={{ bgcolor: '#2196F3', color: '#fff', fontSize: 10, height: 26 }} />
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedBoard('');
                  setSelectedClass('');
                }}
                sx={{ bgcolor: 'white', boxShadow: 1, width: 24, height: 24 }}
              >
                <EditIcon sx={{ fontSize: 12 }} />
              </IconButton>
              <Chip label={`Class ${selectedClass}`} size="small" sx={{ bgcolor: '#9C27B0', color: '#fff', fontSize: 10, height: 26 }} />
              <IconButton
                size="small"
                onClick={() => setSelectedClass('')}
                sx={{ bgcolor: 'white', boxShadow: 1, width: 24, height: 24 }}
              >
                <EditIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: '#2c3e50', fontSize: 15 }}>
              Select Subject
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
              {subjects.map((subject, index) => {
                const color = CARD_COLORS[(index + 2) % CARD_COLORS.length];
                return (
                  <Chip
                    key={subject.id}
                    label={subject.label}
                    onClick={() => setSelectedSubject(subject.id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: color.bg,
                      color: color.text,
                      fontSize: 11,
                      height: 30,
                      px: 1.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: color.border,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 3px 8px ${color.border}40`,
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Fade>
      )}

      {/* Topics & Subtopics + Difficulty Selection */}
      {selectedBoard && selectedClass && selectedSubject && (
        <Fade in={true}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
              <Chip label={selectedBoard} size="small" sx={{ bgcolor: '#2196F3', color: '#fff', fontSize: 10, height: 26 }} />
              <Chip label={`Class ${selectedClass}`} size="small" sx={{ bgcolor: '#9C27B0', color: '#fff', fontSize: 10, height: 26 }} />
              <Chip label={selectedSubject} size="small" sx={{ bgcolor: '#4CAF50', color: '#fff', fontSize: 10, height: 26 }} />
              <IconButton
                size="small"
                onClick={() => setSelectedSubject('')}
                sx={{ bgcolor: 'white', boxShadow: 1, width: 24, height: 24 }}
              >
                <EditIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>

            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, color: '#2c3e50', fontSize: 15 }}>
              Select Topics & Subtopics
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: 11, mb: 1 }}>
              Expand topics to select multiple subtopics
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {topics.map((topic, index) => {
                const color = CARD_COLORS[(index + 3) % CARD_COLORS.length];
                const isExpanded = expandedTopics.has(topic.id);
                const selectionCount = getTopicSelectionCount(topic.id);

                return (
                  <Paper key={topic.id} elevation={1} sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        p: 1.2,
                        bgcolor: color.bg,
                        color: color.text,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleTopicExpansion(topic.id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{topic.label}</Typography>
                        {selectionCount > 0 && (
                          <Chip
                            label={`${selectionCount} selected`}
                            size="small"
                            sx={{ height: 20, fontSize: 9, bgcolor: 'rgba(255,255,255,0.3)', color: '#fff' }}
                          />
                        )}
                      </Box>
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </Box>

                    <Collapse in={isExpanded}>
                      <Box sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                        <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, mb: 0.8, display: 'block' }}>
                          Subtopics:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                          {topic.subtopics.map((subtopic: any) => {
                            const isSelected = isSubtopicSelected(topic.id, subtopic.id);
                            // ─── Flatten prerequisites for tooltip / display ───────────
                            const prereqNames = flattenPrerequisites(subtopic.prerequisites);
                            return (
                              <Box key={subtopic.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                <Chip
                                  label={subtopic.label}
                                  onClick={() =>
                                    handleSubtopicToggle(topic.id, subtopic.id, subtopic.prerequisites)
                                  }
                                  icon={
                                    isSelected
                                      ? <CheckCircleIcon sx={{ fontSize: 14, color: '#fff !important' }} />
                                      : undefined
                                  }
                                  size="small"
                                  sx={{
                                    cursor: 'pointer',
                                    bgcolor: isSelected ? color.border : '#fff',
                                    color: isSelected ? '#fff' : '#333',
                                    border: `1px solid ${color.border}`,
                                    fontSize: 10,
                                    height: 26,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: isSelected ? color.border : `${color.bg}20`,
                                    },
                                  }}
                                />
                                {/* ─── Show prerequisite count badge when selected ─── */}
                                {isSelected && prereqNames.length > 0 && (
                                  <Typography
                                    sx={{
                                      fontSize: 9,
                                      color: '#888',
                                      pl: 0.5,
                                    }}
                                  >
                                    {prereqNames.length} prerequisite{prereqNames.length !== 1 ? 's' : ''}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Box>

            {/* Difficulty Selection */}
            {Object.keys(selectedTopicSubtopics).length > 0 && (
              <>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, color: '#2c3e50', fontSize: 15, mt: 2 }}>
                  Select Difficulty Level
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: 11, mb: 1 }}>
                  Choose difficulty levels for all selected topics
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  {difficultyLevels.map((level) => {
                    const isSelected = selectedDifficulty.includes(level.id);
                    return (
                      <Chip
                        key={level.id}
                        label={level.label}
                        onClick={() => handleDifficultyToggle(level.id)}
                        icon={isSelected ? <CheckCircleIcon sx={{ fontSize: 14, color: '#fff !important' }} /> : level.icon}
                        size="small"
                        sx={{
                          cursor: 'pointer',
                          bgcolor: isSelected ? level.color : '#fff',
                          color: isSelected ? '#fff' : '#333',
                          border: `1px solid ${level.color}`,
                          fontSize: 11,
                          height: 30,
                          px: 1.5,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: isSelected ? level.color : `${level.color}20`,
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        </Fade>
      )}

      {/* Question Types Selection */}
      {Object.keys(selectedTopicSubtopics).length > 0 &&
        selectedDifficulty.length > 0 &&
        selectedQuestionTypes.length === 0 && (
          <Fade in={true}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                <Chip label={selectedBoard} size="small" sx={{ bgcolor: '#2196F3', color: '#fff', fontSize: 10, height: 26 }} />
                <Chip
                  label={`${getTotalSelectedTopics()} Topics`}
                  size="small"
                  sx={{ bgcolor: '#FF9800', color: '#fff', fontSize: 10, height: 26 }}
                />
                <Chip
                  label={`${selectedDifficulty.length} Difficulty`}
                  size="small"
                  sx={{ bgcolor: '#E91E63', color: '#fff', fontSize: 10, height: 26 }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedTopicSubtopics({});
                    setSelectedDifficulty([]);
                  }}
                  sx={{ bgcolor: 'white', boxShadow: 1, width: 24, height: 24 }}
                >
                  <EditIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>

              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, color: '#2c3e50', fontSize: 15 }}>
                Select Question Types
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontSize: 11, mb: 1 }}>
                Choose one or more question formats
              </Typography>

              <Grid container spacing={1}>
                {questionTypes.map((qType) => {
                  const isSelected = selectedQuestionTypes.includes(qType.id);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={qType.id}>
                      <Card
                        elevation={isSelected ? 2 : 1}
                        sx={{
                          borderRadius: 1.5,
                          border: isSelected ? '2px solid' : '1px solid',
                          borderColor: isSelected ? 'primary.main' : '#e0e0e0',
                          bgcolor: isSelected ? 'primary.lighter' : 'transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            transform: 'translateY(-1px)',
                            boxShadow: 2,
                          },
                        }}
                      >
                        <CardActionArea onClick={() => handleQuestionTypeToggle(qType.id)} sx={{ p: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: isSelected ? 'primary.main' : '#f0f0f0',
                                color: isSelected ? '#fff' : '#666',
                              }}
                            >
                              {qType.icon}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: isSelected ? 'primary.main' : 'text.primary',
                                }}
                              >
                                {qType.label}
                              </Typography>
                            </Box>
                            {isSelected && <CheckCircleIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: 10, color: '#666' }}>
                            {qType.description}
                          </Typography>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Fade>
        )}

      {/* Question Count Selection */}
      {Object.keys(selectedTopicSubtopics).length > 0 &&
        selectedDifficulty.length > 0 &&
        selectedQuestionTypes.length > 0 &&
        !selectedQuestionCount && (
          <Fade in={true}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                <Chip label={selectedBoard} size="small" sx={{ bgcolor: '#2196F3', color: '#fff', fontSize: 10, height: 26 }} />
                <Chip
                  label={`${selectedQuestionTypes.length} Types`}
                  size="small"
                  sx={{ bgcolor: '#009688', color: '#fff', fontSize: 10, height: 26 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setSelectedQuestionTypes([])}
                  sx={{ bgcolor: 'white', boxShadow: 1, width: 24, height: 24 }}
                >
                  <EditIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>

              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, color: '#2c3e50', fontSize: 15 }}>
                Select Number of Questions
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontSize: 11, mb: 1 }}>
                How many questions would you like to generate?
              </Typography>

              <Grid container spacing={1}>
                {/* {questionCounts.map((count, index) => {
                  const color = CARD_COLORS[(index + 5) % CARD_COLORS.length];
                  return (
                    <Grid item xs={6} sm={3} key={count.id}>
                      <Card
                        elevation={selectedQuestionCount === count.id ? 2 : 1}
                        sx={{
                          borderRadius: 1.5,
                          border: selectedQuestionCount === count.id ? '2px solid' : '1px solid',
                          borderColor: selectedQuestionCount === count.id ? color.border : '#e0e0e0',
                          bgcolor: selectedQuestionCount === count.id ? `${color.bg}20` : 'transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: color.border,
                            transform: 'translateY(-1px)',
                            boxShadow: 2,
                          },
                        }}
                      >
                        <CardActionArea onClick={() => setSelectedQuestionCount(count.id)} sx={{ p: 2 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography
                              variant="h4"
                              sx={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: selectedQuestionCount === count.id ? color.border : '#666',
                                mb: 0.3,
                              }}
                            >
                              {count.value}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: 10,
                                color: selectedQuestionCount === count.id ? color.border : '#999',
                              }}
                            >
                              Questions
                            </Typography>
                            {selectedQuestionCount === count.id && (
                              <CheckCircleIcon sx={{ fontSize: 18, color: color.border, mt: 0.3 }} />
                            )}
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })} */}
                <QuestionCountSelector
                  counts={questionCounts}
                  selectedValue={selectedQuestionCount}
                  onChange={setSelectedQuestionCount}
                />
              </Grid>
            </Box>
          </Fade>
        )}

      {/* Ready to Generate */}
      {canGenerate && (
        <Fade in={true}>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'success.main', mx: 'auto', mb: 1.5 }}>
              <CheckCircleIcon sx={{ fontSize: 32 }} />
            </Avatar>

            <Typography variant="h5" sx={{ mb: 0.8, fontSize: 18, fontWeight: 600 }}>
              Ready to Generate!
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: 12 }}>
              All selections complete. Click to generate your questions.
            </Typography>

            {!isMobile && (
              <LoadingButton
                buttonName="Generate Questions"
                onClickButton={onGenerate}
                isLoading={isGenerating}
                size="large"
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  px: 3,
                  py: 1.2,
                  borderRadius: 1.5,
                }}
              />
            )}
          </Box>
        </Fade>
      )}

      {isMobile && canGenerate && (
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 1.5,
            borderRadius: 0,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            fullWidth
            onClick={onGenerate}
            disabled={isGenerating}
            startIcon={<QuizIcon />}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              py: 1.2,
              borderRadius: 1.5,
            }}
          >
            {isGenerating ? 'Generating...' : 'Generate Questions'}
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default SelectionFlow;
